import axiosInstance from "src/axios";

const BASE = process.env.NEXT_PUBLIC_BASE_URL ?? "";

// Typed by hand from the backend's order-bundles-view.ts and bundle-admin.service.ts
// (field for field). Clients are never regenerated for this feature.
export type LocalizedText = Record<string, string>;
export type BundleSlotKind = "PHOTO_SIZE" | "PWM_VARIANT" | "PRODUCT";
export type BundleChannel = "ALL" | "WEB" | "MOBILE";

export type BundleSlotCard = {
  id: string;
  kind: BundleSlotKind;
  quantity: number;
  label: string;
  photoSizeId: string | null;
  variantIds: string[];
  productIds: string[];
  definitionId: string | null;
  definitionKind: string | null;
  thumbnailUrl: string | null;
  unitPriceCents: number;
  normalCents: number;
};

export type BundleCard = {
  id: string;
  name: string;
  description: string | null;
  imageUrl: string | null;
  currency: string;
  priceCents: number;
  normalPriceCents: number;
  savingCents: number;
  repeatable: boolean;
  couponsAllowed: boolean;
  slots: BundleSlotCard[];
};

export type AppliedBundle = {
  bundleId: string;
  name: LocalizedText;
  times: number;
  discountCents: number;
  couponsAllowed: boolean;
};

export type BundleSlotProgressView = {
  slotId: string;
  kind: BundleSlotKind;
  label: string;
  /** Raw units toward the next application. */
  have: number;
  /** What the row shows (server rule); absent from older APIs. */
  count?: number;
  need: number;
  /** The row shows a check. */
  full: boolean;
  targetUnits: number;
  photoSizeId: string | null;
  variantIds: string[];
  productIds: string[];
  definitionId: string | null;
  definitionKind: string | null;
};

export type SelectedBundleView = {
  bundleId: string;
  name: string;
  priceCents: number;
  normalPriceCents: number;
  repeatable: boolean;
  timesApplied: number;
  appliedDiscountCents: number;
  complete: boolean;
  /** The rows count toward application timesApplied + 1. Absent from older APIs. */
  towardNext?: boolean;
  /** The saving of application timesApplied + 1, at the ladder rung the cart will be on. */
  nextSavingCents: number;
  /**
   * The normal price of that application, matching nextSavingCents
   * (normalCents − nextSavingCents = priceCents). Absent from older APIs.
   */
  normalCents?: number;
  slots: BundleSlotProgressView[];
};

export type BundleHintView = {
  bundleId: string;
  name: string;
  /** The real saving at the ladder rung the cart will be on. */
  savingCents: number;
  /** The matching normal price (normalCents − savingCents = the paket price). Absent from older APIs. */
  normalCents?: number;
  missing: Array<{
    slotId: string;
    kind: BundleSlotKind;
    label: string;
    units: number;
  }>;
};

export type OrderBundlesView = {
  currency: string;
  applied: AppliedBundle[];
  bundleDiscountCents: number;
  couponDiscountCents: number;
  couponBlockedByBundle: string | null;
  selected: SelectedBundleView | null;
  hint: BundleHintView | null;
  /**
   * A selected or applied paket that has ended (or been switched off) since
   * the cart was last priced: the price was updated without it. Absent from
   * older APIs.
   */
  bundleExpired?: BundleExpiredNote[];
};

/** A paket that expired under the cart; `name` may come localized. */
export type BundleExpiredNote = {
  bundleId?: string;
  name: string | LocalizedText;
};

/** Public: the bundles offered on the web (no X-Client-Channel header means web). */
export async function fetchBundleCards(
  currency: string,
  locale: string,
): Promise<BundleCard[]> {
  try {
    const res = await fetch(
      `${BASE}/bundle?currency=${encodeURIComponent(currency.toLowerCase())}&locale=${encodeURIComponent(locale)}`,
      { headers: { Accept: "application/json" } },
    );
    if (!res.ok) return [];
    const data = (await res.json()) as unknown;
    return Array.isArray(data) ? (data as BundleCard[]) : [];
  } catch {
    return [];
  }
}

/**
 * Behind OrderOwnerGuard: a guest proves ownership with x-guest-token, sent
 * here explicitly rather than trusting the axios default header (which is set
 * only after setGuestTokenCookies has run in this page load).
 */
export async function fetchOrderBundles(
  orderId: string,
  guestToken: string | null | undefined,
  locale: string,
): Promise<OrderBundlesView> {
  const res = await axiosInstance.get(`/order/${orderId}/bundles`, {
    headers: guestToken ? { "x-guest-token": guestToken } : undefined,
    params: { locale },
  });
  return res.data as OrderBundlesView;
}

export async function putSelectedBundle(
  orderId: string,
  guestToken: string | null | undefined,
  bundleId: string | null,
  locale: string,
): Promise<OrderBundlesView> {
  const res = await axiosInstance.put(
    `/order/${orderId}/selected-bundle`,
    { bundleId },
    {
      headers: guestToken ? { "x-guest-token": guestToken } : undefined,
      params: { locale },
    },
  );
  return res.data as OrderBundlesView;
}

// ---------------------------------------------------------------------------
// Admin (/bundle/admin/*): reads any admin, writes superadmin.
// ---------------------------------------------------------------------------

export type AdminBundleSlot = {
  id?: string;
  kind: BundleSlotKind;
  photoSizeId: string | null;
  variantIds: string[];
  productIds: string[];
  quantity: number;
  label: LocalizedText | null;
  position: number;
};

export type AdminBundle = {
  id: string;
  name: LocalizedText;
  description: LocalizedText | null;
  imageKey: string | null;
  imageUrl: string | null;
  price: number | null;
  priceCurrencies: Record<string, number>;
  channel: BundleChannel;
  active: boolean;
  startsAt: string | null;
  endsAt: string | null;
  couponsAllowed: boolean;
  repeatable: boolean;
  sortOrder: number;
  createdBy: string;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
  slots: AdminBundleSlot[];
  stats: Array<{
    currency: string;
    sales: number;
    times: number;
    discountCents: number;
  }>;
};

export type AdminBundleWrite = {
  name: LocalizedText;
  description: LocalizedText | null;
  imageKey: string | null;
  price: number | null;
  priceCurrencies: Record<string, number>;
  channel: BundleChannel;
  active: boolean;
  startsAt: string | null;
  endsAt: string | null;
  couponsAllowed: boolean;
  repeatable: boolean;
  sortOrder: number;
  slots: Array<Omit<AdminBundleSlot, "id">>;
};

export type BundleTargets = {
  photoSizes: Array<{ id: string; name: LocalizedText }>;
  variants: Array<{
    id: string;
    name: LocalizedText;
    sku: string;
    definitionId: string;
    definitionName: LocalizedText;
    definitionKind: string;
  }>;
  products: Array<{ id: string; name: LocalizedText }>;
};

export type BundlePreview = {
  normalPriceCents: number;
  slots: Array<{ unitPriceCents: number; normalCents: number }>;
};

export type AdminBundleApplication = {
  bundleId: string;
  name: LocalizedText;
  times: number;
  discountCents: number;
  currency: string;
  createdAt: string;
};

const ADMIN = `${BASE}/bundle/admin`;

export async function adminListBundles(): Promise<AdminBundle[]> {
  return (await axiosInstance.get(ADMIN)).data as AdminBundle[];
}
export async function adminBundleTargets(): Promise<BundleTargets> {
  return (await axiosInstance.get(`${ADMIN}/targets`)).data as BundleTargets;
}
export async function adminPreviewBundle(
  slots: AdminBundleWrite["slots"],
  currency: string,
): Promise<BundlePreview> {
  return (await axiosInstance.post(`${ADMIN}/preview`, { slots, currency }))
    .data as BundlePreview;
}
export async function adminCreateBundle(
  body: AdminBundleWrite,
): Promise<AdminBundle> {
  return (await axiosInstance.post(ADMIN, body)).data as AdminBundle;
}
export async function adminUpdateBundle(
  id: string,
  body: Partial<AdminBundleWrite>,
): Promise<AdminBundle> {
  return (await axiosInstance.patch(`${ADMIN}/${id}`, body))
    .data as AdminBundle;
}
export async function adminDeleteBundle(
  id: string,
): Promise<{ deleted: true; id: string }> {
  return (await axiosInstance.delete(`${ADMIN}/${id}`)).data;
}
export async function adminBundleApplications(
  orderId: string,
): Promise<AdminBundleApplication[]> {
  const data = (await axiosInstance.get(`${ADMIN}/order/${orderId}`)).data as {
    applications?: AdminBundleApplication[];
  };
  return data?.applications ?? [];
}

/** The server's `problems` (400) or `message`, for an Alert. */
export function bundleErrorMessage(err: unknown, fallback: string): string {
  const data = (
    err as { response?: { data?: { problems?: unknown; message?: unknown } } }
  )?.response?.data;
  if (Array.isArray(data?.problems) && data!.problems.length)
    return (data!.problems as string[]).join(" ");
  if (typeof data?.message === "string" && data.message) return data.message;
  if (Array.isArray(data?.message) && data!.message.length)
    return (data!.message as string[]).join(" ");
  return fallback;
}
